import { createContext, useContext, type ReactNode } from 'react';

import type { CameraPose } from '../engine/camera-playback';
import type { Hotspot3DLabel } from '../schema';

/**
 * Per-element camera playback state, computed by the engine (via
 * `selectLabelFocus`/`selectActiveLabels`) and broadcast to the matching
 * `ThreeDElement` without prop-threading through the row/column tree.
 *
 * No provider mounted → 3D elements render in user-controlled (edit) mode.
 */
export interface ElementCameraPlayback {
  pose: CameraPose;
  labels: Hotspot3DLabel[];
  transitionMs: number;
}

const CameraPlaybackContext = createContext<Record<string, ElementCameraPlayback> | null>(null);

export function CameraPlaybackProvider({
  value,
  children,
}: {
  value: Record<string, ElementCameraPlayback> | null;
  children: ReactNode;
}) {
  return <CameraPlaybackContext.Provider value={value}>{children}</CameraPlaybackContext.Provider>;
}

/** Returns the driven camera state for an element, or null when not in playback. */
export function useElementCameraPlayback(elementId: string): ElementCameraPlayback | null {
  const map = useContext(CameraPlaybackContext);
  return map?.[elementId] ?? null;
}
